import { Fragment, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import MetaData from "../layouts/MetaData";
import formatDate from "../../utils/formatDate";
import { toast } from "react-toastify";
import {
    updatePatientMedical,
    updatePatient,
    deletePatientFile,
    deletePatient,
    getpatient
} from "../../actions/patientActions";
import {
    clearFileDeleted,
    clearError,
    clearPatientDeleted
} from "../../slices/patientSlice";
import { useNavigate } from "react-router-dom";
import { getAllStaff } from "../../actions/staffActions";
import { getTreatments } from "../../actions/treatmentActions";
import { getPatientBills } from "../../actions/billingActions";
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import AppDatePicker from '../common/AppDatePicker';

// ===============================
// 🔹 HELPERS
// ===============================
const getFileName = (file) =>
    file?.image
        ? decodeURIComponent(file.image.split("/").pop())
        : "Unknown file";

const getFileIcon = (type) => (type === "pdf" ? "📄" : "🖼");

// ===============================
// 🔹 FILE ITEM (WITH CONFIRM)
// ===============================
function FileItem({ file, onDelete, isAdmin, type }) {
    const [showConfirm, setShowConfirm] = useState(false);

    return (
        <div className="d-flex justify-content-between align-items-center mb-2 p-1 rounded bg-light border">
            <a href={file.image} target="_blank" rel="noreferrer" className="text-decoration-none text-truncate me-2 small">
                {getFileIcon(file.fileType)} {getFileName(file)}
            </a>

            {isAdmin && (
                <div>
                    {!showConfirm ? (
                        <button
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => setShowConfirm(true)}
                            title="Delete file"
                        >
                            <i className="fa fa-trash"></i>
                        </button>
                    ) : (
                        <div className="d-inline-flex align-items-center gap-1">
                            <button
                                className="btn btn-danger btn-sm py-0 px-1"
                                style={{ fontSize: '0.7rem' }}
                                onClick={() => {
                                    onDelete(file.image, type);
                                    setShowConfirm(false);
                                }}
                            >
                                Yes
                            </button>
                            <button
                                className="btn btn-secondary btn-sm py-0 px-1"
                                style={{ fontSize: '0.7rem' }}
                                onClick={() => setShowConfirm(false)}
                            >
                                No
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ===============================
// 🔹 FILE PREVIEW
// ===============================
function FilePreview({ files, preview, setFiles, setPreview }) {
    const removeFile = (index) => {
        const newFiles = [...files];
        const newPreview = [...preview];

        newFiles.splice(index, 1);
        newPreview.splice(index, 1);

        setFiles(newFiles);
        setPreview(newPreview);
    };

    return (
        <div className="mt-2">
            {preview.map((item, i) => (
                <div
                    key={i}
                    style={{
                        display: "inline-block",
                        width: "60px",
                        height: "60px",
                        position: "relative",
                        margin: "4px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        overflow: "hidden"
                    }}
                >
                    <span
                        onClick={() => removeFile(i)}
                        style={{
                            position: "absolute",
                            top: "1px",
                            right: "3px",
                            color: "red",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "bold",
                            zIndex: 2,
                            background: "rgba(255,255,255,0.7)",
                            borderRadius: "50%",
                            padding: "0 3px"
                        }}
                    >
                        ✖
                    </span>

                    {item === "pdf" ? (
                        <div className="d-flex h-100 justify-content-center align-items-center">
                            📄
                        </div>
                    ) : (
                        <img
                            src={item}
                            alt=""
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                            }}
                        />
                    )}
                </div>
            ))}
        </div>
    );
}

// ===============================
// 🔹 MAIN COMPONENT
// ===============================
export default function OutPatient({ patient }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const id = patient?.patientId || patient?._id || patient?.id;

    const { user } = useSelector(state => state.authState);
    const { isFileDeleted, isPatientDeleted, error } = useSelector(state => state.patientState);
    const { staffList } = useSelector(state => state.staffState);
    const { treatments: treatmentsList } = useSelector(state => state.treatmentState);
    const { bills = [] } = useSelector(state => state.billingState);

    const isAdmin = user?.role === "admin" || user?.role === "master";
    const canEditMedical = user?.role !== "manager";

    const getTodayDate = () => {
        const today = new Date();
        const offset = today.getTimezoneOffset();
        return new Date(today.getTime() - offset * 60000)
            .toISOString()
            .split("T")[0];
    };

    const getTodayTimeInput = () => {
        const now = new Date();
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    };

    const formatTimeForDisplay = (timeStr) => {
        if (!timeStr) return '';
        if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
            return timeStr;
        }
        const parts = timeStr.split(':');
        if (parts.length >= 2) {
            let hour = parseInt(parts[0], 10);
            const minute = parts[1];
            if (isNaN(hour)) return timeStr;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            hour = hour % 12 || 12;
            const padHour = hour < 10 ? `0${hour}` : hour;
            return `${padHour}:${minute} ${ampm}`;
        }
        return timeStr;
    };

    const toTimeInputFormat = (timeStr) => {
        if (!timeStr) return '';
        const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
        if (match) {
            let hour = parseInt(match[1], 10);
            const min = match[2];
            const ampm = match[3] ? match[3].toUpperCase() : null;
            if (ampm === 'PM' && hour < 12) hour += 12;
            if (ampm === 'AM' && hour === 12) hour = 0;
            return `${String(hour).padStart(2, '0')}:${min}`;
        }
        return '';
    };

    const [visitDate, setVisitDate] = useState(getTodayDate());
    const [visitTime, setVisitTime] = useState(getTodayTimeInput());
    const [doctor, setDoctor] = useState("");

    const [selectedTreatments, setSelectedTreatments] = useState([]);
    const [customTreatment, setCustomTreatment] = useState("");
    const prevPatientIdRef = useRef(id);
    const [isEditingRemarks, setIsEditingRemarks] = useState(false);
    const [remarks, setRemarks] = useState(patient.remarks || "");

    const [activeFileTab, setActiveFileTab] = useState('reports'); // 'reports' | 'casesheets'

    const [reportFiles, setReportFiles] = useState([]);
    const [reportPreview, setReportPreview] = useState([]);

    const [caseFiles, setCaseFiles] = useState([]);
    const [casePreview, setCasePreview] = useState([]);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [visitDeleteModal, setVisitDeleteModal] = useState({ show: false, visit: null, name: '' });

    // Sync remarks when patient changes
    useEffect(() => {
        if (patient) {
            setRemarks(patient.remarks || "");
        }
    }, [patient]);

    // Ensure staff, treatments, and patient bills are loaded
    useEffect(() => {
        dispatch(getTreatments());
        if (isAdmin) {
            dispatch(getAllStaff());
        }
        const targetId = patient?.patientId || patient?._id || patient?.id;
        if (targetId && (!bills || bills.length === 0)) {
            dispatch(getPatientBills(targetId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isAdmin, patient]);

    // Doctors list
    const liveBalanceDue = (() => {
        if (bills && bills.length > 0) {
            return bills.reduce((acc, b) => b.status !== 'Cancelled' ? acc + Number(b.balanceDue || 0) : acc, 0);
        }
        return Number(patient.balanceDue || patient.totalDue || 0);
    })();

    const doctorList = (staffList || []).filter(
        s => s.designation && s.designation.toLowerCase().includes('doctor')
    );

    // AUTO-SELECT SINGLE VALUE: If only 1 doctor exists in clinic, auto-select!
    useEffect(() => {
        if (!doctor && doctorList.length === 1) {
            setDoctor(doctorList[0].name);
        }
    }, [doctorList, doctor]);

    // AUTO-SELECT SINGLE VALUE: If only 1 treatment exists in master list and none selected, auto-select!
    useEffect(() => {
        const activeTreatments = (treatmentsList || []).filter(t => t.isActive !== false);
        if (activeTreatments.length === 1 && selectedTreatments.length === 0) {
            setSelectedTreatments([activeTreatments[0].name]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treatmentsList]);

    // Valid non-cancelled bills for fetching details
    const validBills = (bills || []).filter(b => b.status !== 'Cancelled');

    // Existing treatments already recorded on this visit date
    const existingTreatmentsOnDate = (patient?.visits || [])
        .filter(v => {
            if (!v.date || !visitDate) return false;
            try {
                const vDate = new Date(v.date).toISOString().split('T')[0];
                const targetDate = new Date(visitDate).toISOString().split('T')[0];
                return vDate === targetDate;
            } catch (e) {
                return false;
            }
        })
        .map(v => (v.treatment || '').trim().toLowerCase())
        .filter(Boolean);

    // FETCH FROM BILL: Auto-populate doctor, date & treatments from an invoice
    const handleImportFromBill = (bill) => {
        if (!bill) return;

        // 1. Visit Date from bill createdAt
        if (bill.createdAt) {
            try {
                const bDate = new Date(bill.createdAt).toISOString().split('T')[0];
                setVisitDate(bDate);
            } catch (e) {}
        }

        // 2. Doctor from bill
        if (bill.doctor) {
            setDoctor(bill.doctor);
        } else if (isAdmin && doctorList.length === 1) {
            setDoctor(doctorList[0].name);
        }

        // 3. Treatments from bill items
        const billTreatments = (bill.items || [])
            .map(i => (i.treatment || i.description || i.name || '').trim())
            .filter(Boolean);

        if (billTreatments.length === 0) {
            toast.info(`Bill ${bill.invoiceNumber} has no treatment items`);
            return;
        }

        let addedCount = 0;
        setSelectedTreatments(prev => {
            const updated = [...prev];
            billTreatments.forEach(tName => {
                const lower = tName.toLowerCase();
                const alreadySelected = updated.some(item => item.toLowerCase() === lower);
                const alreadyRecorded = existingTreatmentsOnDate.includes(lower);
                if (!alreadySelected && !alreadyRecorded) {
                    updated.push(tName);
                    addedCount++;
                }
            });
            return updated;
        });

        if (addedCount > 0) {
            toast.success(`Fetched ${addedCount} treatment${addedCount > 1 ? 's' : ''} from Bill ${bill.invoiceNumber}`);
        } else {
            toast.info(`Treatments from Bill ${bill.invoiceNumber} are already added/recorded`);
        }
    };

    const handleSaveRemarks = async () => {
        const formData = new FormData();
        formData.append("remarks", remarks);
        await dispatch(updatePatient(id, formData));
        setIsEditingRemarks(false);
        toast.success("Remarks updated successfully");
        dispatch(getpatient(id));
    };

    const openDeleteVisitModal = (visit) => {
        setVisitDeleteModal({ show: true, visit, name: `${formatDate(visit.date)} - ${visit.treatment}` });
    };

    const confirmDeleteVisit = async () => {
        const { visit } = visitDeleteModal;
        if (!visit) return;

        await dispatch(updatePatientMedical(id, {
            deleteVisit: {
                _id: visit._id
            }
        }));

        toast.success("Visit deleted");
        setVisitDeleteModal({ show: false, visit: null, name: '' });
        dispatch(getpatient(id));
    };

    const handleDateChange = (newDate) => {
        setVisitDate(newDate);
        if (patient?.visits) {
            const existing = patient.visits.find(v => {
                const d = v.date ? new Date(v.date).toISOString().split('T')[0] : '';
                return d === newDate && v.time;
            });
            if (existing && existing.time) {
                const formatted = toTimeInputFormat(existing.time);
                if (formatted) setVisitTime(formatted);
            }
        }
    };

    useEffect(() => {
        if (prevPatientIdRef.current !== id) {
            prevPatientIdRef.current = id;
            setSelectedTreatments([]);
            setCustomTreatment("");
            const today = getTodayDate();
            setVisitDate(today);
            if (patient?.visits) {
                const existing = patient.visits.find(v => {
                    const d = v.date ? new Date(v.date).toISOString().split('T')[0] : '';
                    return d === today && v.time;
                });
                if (existing && existing.time) {
                    setVisitTime(toTimeInputFormat(existing.time) || getTodayTimeInput());
                } else {
                    setVisitTime(getTodayTimeInput());
                }
            } else {
                setVisitTime(getTodayTimeInput());
            }
        }
    }, [id, patient]);

    // Group visits by date
    const groupedVisits = [];
    if (patient.visits) {
        patient.visits.slice().reverse().forEach(visit => {
            const dateStr = formatDate(visit.date);
            let dayGroup = groupedVisits.find(g => g.dateStr === dateStr);
            if (!dayGroup) {
                dayGroup = { dateStr, consultingTime: visit.time || null, doctors: [] };
                groupedVisits.push(dayGroup);
            } else if (!dayGroup.consultingTime && visit.time) {
                dayGroup.consultingTime = visit.time;
            }

            const docName = visit.doctor || "No Doctor Assigned";
            let docGroup = dayGroup.doctors.find(d => d.name === docName);
            if (!docGroup) {
                docGroup = { name: docName, visits: [] };
                dayGroup.doctors.push(docGroup);
            }

            docGroup.visits.push(visit);
        });
    }

    // Effect handlers
    useEffect(() => {
        dispatch(clearFileDeleted());
        dispatch(clearPatientDeleted());
        dispatch(clearError());
        return () => {
            dispatch(clearFileDeleted());
            dispatch(clearPatientDeleted());
            dispatch(clearError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isFileDeleted) {
            toast.success("File deleted");
            dispatch(clearFileDeleted());
            dispatch(getpatient(id));
        }

        if (isPatientDeleted) {
            dispatch(clearPatientDeleted());
            toast.success("Patient deleted successfully");
            navigate("/admin/patients");
        }

        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [isFileDeleted, isPatientDeleted, error, dispatch, id, navigate]);

    const handleFileChange = (e, setFiles, setPreview) => {
        const files = Array.from(e.target.files);

        files.forEach(file => {
            if (file.type.startsWith("image")) {
                const reader = new FileReader();
                reader.onload = () => {
                    if (reader.readyState === 2) {
                        setPreview(old => [...old, reader.result]);
                        setFiles(old => [...old, file]);
                    }
                };
                reader.readAsDataURL(file);
            } else {
                setPreview(old => [...old, "pdf"]);
                setFiles(old => [...old, file]);
            }
        });

        e.target.value = null;
    };

    const handleDeleteFile = (fileUrl, type) => {
        dispatch(deletePatientFile(id, { fileUrl, type }));
    };

    const confirmDelete = () => {
        dispatch(deletePatient(id));
    };

    const handleSave = async () => {
        if (!visitDate) {
            return toast.error("Please pick a visit date");
        }
        if (!selectedTreatments || selectedTreatments.length === 0) {
            return toast.error("Please add at least one treatment");
        }
        if (isAdmin && !doctor) {
            return toast.error("Please select a doctor for this visit");
        }

        const formattedTime = formatTimeForDisplay(visitTime);

        await dispatch(updatePatientMedical(id, {
            visit: {
                date: visitDate,
                time: formattedTime,
                treatments: selectedTreatments,
                doctor: isAdmin ? doctor : (user?.name || undefined)
            }
        }));

        toast.success(`${selectedTreatments.length} treatment${selectedTreatments.length > 1 ? 's' : ''} added to visit`);

        setSelectedTreatments([]);
        setCustomTreatment("");
        dispatch(getpatient(id));
    };

    const uploadReports = async () => {
        if (!reportFiles.length) return;
        const formData = new FormData();
        reportFiles.forEach(file => formData.append("images", file));
        await dispatch(updatePatient(id, formData));
        setReportFiles([]);
        setReportPreview([]);
        toast.success("Reports uploaded");
    };

    const uploadCaseSheets = async () => {
        if (!caseFiles.length) return;
        const formData = new FormData();
        caseFiles.forEach(file => formData.append("caseimages", file));
        await dispatch(updatePatient(id, formData));
        setCaseFiles([]);
        setCasePreview([]);
        toast.success("Case sheets uploaded");
    };

    return (
        <Fragment>
            <MetaData title={patient.name} />

            <div className="row g-3">
                {/* ========================================================
                    LEFT COLUMN: Compact Profile & Documents (col-12 col-lg-4)
                    ======================================================== */}
                <div className="col-12 col-lg-4 d-flex flex-column gap-3">
                    {/* PATIENT PROFILE CARD */}
                    <div className="card shadow-sm p-3">
                        <div className="text-center mb-2">
                            <div 
                                className="rounded-circle bg-primary-subtle text-primary fw-bold d-inline-flex align-items-center justify-content-center mb-2 shadow-sm"
                                style={{ width: '56px', height: '56px', fontSize: '1.4rem' }}
                            >
                                {patient.name ? patient.name.charAt(0).toUpperCase() : 'P'}
                            </div>
                            <h5 className="fw-bold mb-1">{patient.name}</h5>
                            <div className="d-flex justify-content-center gap-1 flex-wrap">
                                <span className="badge bg-primary">{patient.patientId}</span>
                                {patient.category && (
                                    <span className="badge bg-secondary">{patient.category}</span>
                                )}
                            </div>
                        </div>

                        <hr className="my-2" />

                        <div className="small">
                            <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted fw-semibold">Age / Gender:</span>
                                <span>{patient.age ? `${patient.age} yrs` : '-'} / {patient.gender || '-'}</span>
                            </div>
                            {patient.dob && (
                                <div className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted fw-semibold">Date of Birth:</span>
                                    <span>{formatDate(patient.dob)}</span>
                                </div>
                            )}
                            <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted fw-semibold">Phone:</span>
                                <span>
                                    {patient.phone ? (
                                        <a href={`tel:${patient.phone}`} className="text-decoration-none">
                                            <i className="fa fa-phone me-1 text-success"></i>{patient.phone}
                                        </a>
                                    ) : '-'}
                                </span>
                            </div>
                            <div className="py-1">
                                <span className="text-muted fw-semibold d-block">Address:</span>
                                <span className="text-secondary">{patient.address || '-'}</span>
                            </div>
                        </div>

                        {/* Quick Stats Badges */}
                        <div className="d-flex gap-2 mt-2 pt-2 border-top">
                            <div className="flex-fill text-center p-1 bg-light rounded border">
                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Visits</small>
                                <strong className="text-primary">{groupedVisits.length}</strong>
                            </div>
                            <div className="flex-fill text-center p-1 bg-light rounded border">
                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Bills</small>
                                <strong className="text-success">{bills?.length || 0}</strong>
                            </div>
                            <div className="flex-fill text-center p-1 bg-light rounded border">
                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Due</small>
                                <strong className={liveBalanceDue > 0 ? "text-danger" : "text-success"}>
                                    {liveBalanceDue > 0 ? `₹${liveBalanceDue.toLocaleString('en-IN')}` : "Paid"}
                                </strong>
                            </div>
                        </div>

                        {/* Remarks / Disease Info */}
                        <div className="mt-3 p-2 bg-light rounded border">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong className="text-primary small">
                                    <i className="fa fa-notes-medical me-1"></i>Remarks / Condition
                                </strong>
                                {canEditMedical && !isEditingRemarks && (
                                    <button
                                        className="btn btn-sm btn-link text-primary p-0 text-decoration-none"
                                        onClick={() => setIsEditingRemarks(true)}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        <i className="fa fa-pencil me-1"></i>Edit
                                    </button>
                                )}
                            </div>

                            {isEditingRemarks ? (
                                <div>
                                    <textarea
                                        className="form-control form-control-sm mb-2"
                                        rows="3"
                                        placeholder="e.g. Back pain, Lower spine stiffness, Diabetes"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    />
                                    <div className="d-flex gap-2 justify-content-end">
                                        <button
                                            className="btn btn-sm btn-secondary py-0 px-2"
                                            style={{ fontSize: '0.75rem' }}
                                            onClick={() => {
                                                setRemarks(patient.remarks || "");
                                                setIsEditingRemarks(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="btn btn-sm btn-success py-0 px-2"
                                            style={{ fontSize: '0.75rem' }}
                                            onClick={handleSaveRemarks}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ fontSize: '0.85rem' }}>
                                    {patient.remarks ? (
                                        <span className="text-dark" style={{ whiteSpace: 'pre-line' }}>{patient.remarks}</span>
                                    ) : (
                                        <em className="text-muted small">No remarks added. Click Edit to add.</em>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* FILES CARD (Reports & Case sheets combined with sub-tabs) */}
                    <div className="card shadow-sm p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0 text-secondary">
                                <i className="fa fa-folder-open me-1 text-warning"></i> Documents
                            </h6>
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 ${activeFileTab === 'reports' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => setActiveFileTab('reports')}
                                >
                                    Reports ({patient.images?.length || 0})
                                </button>
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 ${activeFileTab === 'casesheets' ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => setActiveFileTab('casesheets')}
                                >
                                    Case Sheets ({patient.caseimages?.length || 0})
                                </button>
                            </div>
                        </div>

                        {/* Reports Tab */}
                        {activeFileTab === 'reports' && (
                            <div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {(!patient.images || patient.images.length === 0) ? (
                                        <div className="text-muted small text-center py-3 bg-light rounded border mb-2">
                                            No reports uploaded
                                        </div>
                                    ) : (
                                        patient.images.map((file, i) => (
                                            <FileItem key={i} file={file} isAdmin={canEditMedical} type="images" onDelete={handleDeleteFile} />
                                        ))
                                    )}
                                </div>

                                {canEditMedical && (
                                    <div className="pt-2 border-top mt-2">
                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <label className="btn btn-sm btn-outline-primary mb-0 py-1" style={{ fontSize: '0.75rem' }}>
                                                <i className="fa fa-paperclip me-1"></i> Choose Reports
                                                <input type="file" multiple hidden onChange={(e) =>
                                                    handleFileChange(e, setReportFiles, setReportPreview)
                                                } />
                                            </label>
                                            {reportFiles.length > 0 && (
                                                <button className="btn btn-sm btn-primary py-1" style={{ fontSize: '0.75rem' }} onClick={uploadReports}>
                                                    <i className="fa fa-upload me-1"></i> Upload ({reportFiles.length})
                                                </button>
                                            )}
                                        </div>
                                        <FilePreview files={reportFiles} preview={reportPreview} setFiles={setReportFiles} setPreview={setReportPreview} />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Case Sheets Tab */}
                        {activeFileTab === 'casesheets' && (
                            <div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {(!patient.caseimages || patient.caseimages.length === 0) ? (
                                        <div className="text-muted small text-center py-3 bg-light rounded border mb-2">
                                            No case sheets uploaded
                                        </div>
                                    ) : (
                                        patient.caseimages.map((file, i) => (
                                            <FileItem key={i} file={file} isAdmin={canEditMedical} type="caseimages" onDelete={handleDeleteFile} />
                                        ))
                                    )}
                                </div>

                                {canEditMedical && (
                                    <div className="pt-2 border-top mt-2">
                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <label className="btn btn-sm btn-outline-warning text-dark mb-0 py-1" style={{ fontSize: '0.75rem' }}>
                                                <i className="fa fa-paperclip me-1"></i> Choose Case Sheets
                                                <input type="file" multiple hidden onChange={(e) =>
                                                    handleFileChange(e, setCaseFiles, setCasePreview)
                                                } />
                                            </label>
                                            {caseFiles.length > 0 && (
                                                <button className="btn btn-sm btn-warning py-1" style={{ fontSize: '0.75rem' }} onClick={uploadCaseSheets}>
                                                    <i className="fa fa-upload me-1"></i> Upload ({caseFiles.length})
                                                </button>
                                            )}
                                        </div>
                                        <FilePreview files={caseFiles} preview={casePreview} setFiles={setCaseFiles} setPreview={setCasePreview} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ========================================================
                    RIGHT COLUMN: Add Visit Treatment & Visit History (col-12 col-lg-8)
                    ======================================================== */}
                <div className="col-12 col-lg-8 d-flex flex-column gap-3">
                    {/* Top Action: Delete Patient */}
                    {isAdmin && (
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
                                <i className="fa fa-trash me-1"></i> Delete Patient
                            </button>
                        </div>
                    )}

                    {/* ADD VISIT TREATMENT CARD */}
                    {canEditMedical && (
                        <div className="card shadow-sm p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <h5 className="fw-bold mb-0 text-primary">
                                    <i className="fa fa-calendar-plus me-2"></i>
                                    Add Visit Treatment
                                </h5>

                                {/* Quick Fetch from Latest Bill button */}
                                {validBills && validBills.length === 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        style={{ fontSize: '0.78rem' }}
                                        onClick={() => handleImportFromBill(validBills[0])}
                                        title="Fetch doctor, date & treatments from Bill"
                                    >
                                        <i className="fa fa-receipt me-1"></i> Fetch from Bill ({validBills[0].invoiceNumber})
                                    </button>
                                )}
                            </div>

                            {/* Fetch from Bill Dropdown if multiple bills exist */}
                            {validBills && validBills.length > 1 && (
                                <div className="mb-3 p-2 bg-primary-subtle border border-primary-subtle rounded">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="small text-primary fw-bold text-nowrap">
                                            <i className="fa fa-receipt me-1"></i> Fetch from Bill:
                                        </span>
                                        <div className="flex-grow-1">
                                            <select
                                                className="form-select form-select-sm"
                                                defaultValue=""
                                                onChange={(e) => {
                                                    const bId = e.target.value;
                                                    if (!bId) return;
                                                    const selected = validBills.find(b => String(b._id || b.id) === String(bId));
                                                    if (selected) handleImportFromBill(selected);
                                                    e.target.value = "";
                                                }}
                                            >
                                                <option value="">-- Choose a bill to auto-populate treatments & doctor --</option>
                                                {validBills.map(b => (
                                                    <option key={b._id || b.id} value={b._id || b.id}>
                                                        {b.invoiceNumber} ({formatDate(b.createdAt)}) {b.doctor ? `[${b.doctor}]` : ''} - {(b.items || []).map(i => i.treatment || i.description || i.name).filter(Boolean).join(', ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CONSULTING DOCTOR */}
                            {isAdmin && (
                                <div className="mb-2">
                                    <label className="form-label small text-muted mb-1 fw-semibold">Consulting Doctor</label>
                                    <select
                                        className="form-control"
                                        value={doctor}
                                        onChange={(e) => setDoctor(e.target.value)}
                                    >
                                        <option value="">-- Select Doctor --</option>
                                        {doctorList.map(s => (
                                            <option key={s._id} value={s.name}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* DATE & TIME PICKERS */}
                            <div className="row g-2 mb-3">
                                <div className="col-12 col-md-6">
                                    <AppDatePicker
                                        label="Visit Date"
                                        value={visitDate}
                                        onChange={handleDateChange}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label small text-muted mb-1 fw-semibold">Consulting Time</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white text-muted">
                                            <i className="fa fa-clock"></i>
                                        </span>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={visitTime}
                                            onChange={(e) => setVisitTime(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* PRESCRIBE TREATMENTS */}
                            <div className="mb-3">
                                <label className="form-label small text-muted mb-1 fw-semibold">
                                    <i className="fa fa-stethoscope me-1 text-primary"></i> Prescribe Treatments
                                </label>

                                <select
                                    className="form-select mb-2"
                                    value=""
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) return;
                                        const lowerVal = val.trim().toLowerCase();
                                        if (selectedTreatments.some(st => st.trim().toLowerCase() === lowerVal)) {
                                            toast.warning(`"${val}" is already added`);
                                            return;
                                        }
                                        if (existingTreatmentsOnDate.includes(lowerVal)) {
                                            toast.warning(`"${val}" is already recorded for this visit date`);
                                            return;
                                        }
                                        setSelectedTreatments(prev => [...prev, val]);
                                    }}
                                >
                                    <option value="">-- Click to add treatment from master list --</option>
                                    {treatmentsList
                                        ?.filter(t => t.isActive !== false)
                                        .map(t => {
                                            const lowerName = (t.name || '').trim().toLowerCase();
                                            const isSelected = selectedTreatments.some(st => st.trim().toLowerCase() === lowerName);
                                            const isAlreadyRecorded = existingTreatmentsOnDate.includes(lowerName);
                                            const isDisabled = isSelected || isAlreadyRecorded;
                                            return (
                                                <option key={t._id || t.name} value={t.name} disabled={isDisabled}>
                                                    {t.name}{isDisabled ? (isAlreadyRecorded ? " (Already recorded for this date)" : " (Already added)") : ""}
                                                </option>
                                            );
                                    })}
                                </select>

                                {/* Custom treatment input */}
                                <div className="input-group input-group-sm mb-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Or type custom treatment..."
                                        value={customTreatment}
                                        onChange={(e) => setCustomTreatment(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const trimmed = customTreatment.trim();
                                                if (!trimmed) return;
                                                const lowerTrimmed = trimmed.toLowerCase();
                                                if (selectedTreatments.some(st => st.trim().toLowerCase() === lowerTrimmed)) {
                                                    toast.warning(`"${trimmed}" is already added`);
                                                    return;
                                                }
                                                if (existingTreatmentsOnDate.includes(lowerTrimmed)) {
                                                    toast.warning(`"${trimmed}" is already recorded for this visit date`);
                                                    return;
                                                }
                                                setSelectedTreatments(prev => [...prev, trimmed]);
                                                setCustomTreatment("");
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            const trimmed = customTreatment.trim();
                                            if (!trimmed) return;
                                            const lowerTrimmed = trimmed.toLowerCase();
                                            if (selectedTreatments.some(st => st.trim().toLowerCase() === lowerTrimmed)) {
                                                toast.warning(`"${trimmed}" is already added`);
                                                return;
                                            }
                                            if (existingTreatmentsOnDate.includes(lowerTrimmed)) {
                                                toast.warning(`"${trimmed}" is already recorded for this visit date`);
                                                return;
                                            }
                                            setSelectedTreatments(prev => [...prev, trimmed]);
                                            setCustomTreatment("");
                                        }}
                                        disabled={!customTreatment.trim()}
                                    >
                                        <i className="fa fa-plus me-1"></i> Add
                                    </button>
                                </div>

                                {/* Selected Treatments Tags List */}
                                {selectedTreatments.length > 0 ? (
                                    <div className="p-2 border rounded bg-white">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.72rem' }}>
                                                Selected Treatments ({selectedTreatments.length}):
                                            </small>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => setSelectedTreatments([])}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                            {selectedTreatments.map((t, idx) => (
                                                <span
                                                    key={idx}
                                                    className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1.5 d-inline-flex align-items-center gap-1"
                                                    style={{ fontSize: '0.82rem', fontWeight: 500 }}
                                                >
                                                    <i className="fa fa-check-circle text-primary"></i>
                                                    {t}
                                                    <button
                                                        type="button"
                                                        className="btn-close ms-1"
                                                        style={{ fontSize: '0.55rem' }}
                                                        onClick={() => setSelectedTreatments(prev => prev.filter(item => item !== t))}
                                                        aria-label="Remove"
                                                    ></button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted small p-2 border rounded bg-light text-center" style={{ fontSize: '0.8rem' }}>
                                        <i className="fa fa-info-circle me-1"></i> No treatments selected yet. Pick from dropdown, type custom, or fetch from bill above.
                                    </div>
                                )}
                            </div>

                            <button
                                className="btn btn-primary w-100 py-2 fw-semibold shadow-sm"
                                onClick={handleSave}
                                disabled={selectedTreatments.length === 0}
                            >
                                <i className="fa fa-check me-2"></i>
                                {selectedTreatments.length > 0
                                    ? `Save Visit (${selectedTreatments.length} Treatment${selectedTreatments.length > 1 ? 's' : ''})`
                                    : 'Select Treatments to Save'}
                            </button>
                        </div>
                    )}

                    {/* VISIT HISTORY CARD (scrollable, contained, balanced height) */}
                    <div className="card shadow-sm p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0 text-primary">
                                <i className="fa fa-history me-2"></i>
                                Visit History
                            </h5>
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                                {groupedVisits.length} Visit{groupedVisits.length !== 1 ? 's' : ''} ({patient.visits?.length || 0} Treatment{patient.visits?.length !== 1 ? 's' : ''})
                            </span>
                        </div>

                        <div style={{ maxHeight: '440px', overflowY: 'auto', paddingRight: '4px' }}>
                            {!patient.visits || patient.visits.length === 0 ? (
                                <div className="text-muted text-center py-4 bg-light rounded border">
                                    <i className="fa fa-notes-medical fa-2x mb-2 text-secondary opacity-50 d-block"></i>
                                    No visit history recorded yet. Use the form above to add treatments.
                                </div>
                            ) : (
                                groupedVisits.map(dayGroup => (
                                    <div key={dayGroup.dateStr} className="border rounded p-3 mb-2 bg-light">
                                        <h6 className="border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2 text-primary fw-bold">
                                            <span className="d-flex align-items-center gap-2">
                                                <i className="fa fa-calendar-alt"></i>
                                                <span>{dayGroup.dateStr}</span>
                                            </span>
                                            {dayGroup.consultingTime && (
                                                <span className="badge bg-white text-secondary border fw-normal py-1 px-2" style={{ fontSize: '0.8rem' }}>
                                                    <i className="fa fa-clock me-1 text-primary"></i>Consulting Time: <strong className="text-dark">{formatTimeForDisplay(dayGroup.consultingTime)}</strong>
                                                </span>
                                            )}
                                        </h6>
                                        
                                        {dayGroup.doctors.map(docGroup => (
                                            <div key={docGroup.name} className="mb-2 ms-2">
                                                <strong className="text-dark d-block mb-1 small">
                                                    <i className="fa fa-user-md me-1 text-secondary"></i> {docGroup.name}
                                                </strong>
                                                <ul className="mb-0" style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                                                    {docGroup.visits.map(v => (
                                                        <li key={v._id} className="d-flex justify-content-between align-items-center mb-1">
                                                            <span style={{ fontSize: '0.92rem' }}>{v.treatment}</span>
                                                            {isAdmin && (
                                                                <button
                                                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                                    style={{ textDecoration: 'none' }}
                                                                    onClick={() => openDeleteVisitModal(v)}
                                                                    title="Delete treatment"
                                                                >
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <DeleteConfirmModal 
                show={visitDeleteModal.show}
                onClose={() => setVisitDeleteModal({ show: false, visit: null, name: '' })}
                onConfirm={confirmDeleteVisit}
                itemName={visitDeleteModal.name}
            />

            <DeleteConfirmModal 
                show={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                itemName={patient?.name || 'this patient'}
            />
        </Fragment>
    );
}
